import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * A custom element that displays the selected variant's 'sided' metafield.
 * Listens for variant update events and swaps in the freshly-rendered value,
 * since metafields aren't included in the lightweight variant JSON used for
 * simple fields like SKU or price.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} metafieldValue
 *
 * @extends {Component<Refs>}
 */
class VariantMetafieldSided extends Component {
  requiredRefs = ['metafieldValue'];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateMetafield);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateMetafield);
  }

  /**
   * Updates the displayed metafield value.
   * @param {CustomEvent} event - The variant update event.
   */
  updateMetafield = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newComponent = event.detail.data.html.querySelector(
      `variant-metafield-sided[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newComponent) return;

    const newValue = newComponent.querySelector('[ref="metafieldValue"]');
    if (newValue && this.refs.metafieldValue) {
      this.refs.metafieldValue.replaceWith(newValue);
    }

    this.style.display = newComponent.style.display;
  };
}

if (!customElements.get('variant-metafield-sided')) {
  customElements.define('variant-metafield-sided', VariantMetafieldSided);
}
